/**
 * Remote-update refresh gate (v3 model: Instant vs Delayed updates).
 *
 * The native app loads the live web bundle via `server.url`, so a web deploy IS
 * an app update. This module decides how existing sessions get pulled onto a new
 * bundle when an admin issues a force-refresh:
 *
 *   - First run (no stored token): silently adopt the current token, show NOTHING.
 *     (A fresh install is already on the newest bundle; greeting it with an update
 *     prompt gets the app rejected — Apple 2.1a.)
 *   - refreshMode 'force'  (Instant Update): silently hard-refresh, respecting any
 *     in-flight "holds" (live game, upload) so we never interrupt.
 *   - refreshMode 'banner' (Delay Update): a non-blocking top banner; the user taps
 *     Reload when ready.
 *   - Blocking overlay survives ONLY as a loop-guard fail-safe (a silent refresh was
 *     already attempted this session but the token still mismatches) and for
 *     `store-update` (native binary below minIos/minAndroidVersion).
 */
import { Capacitor } from '@capacitor/core';
import type { AppPolicy } from '@shared/types';
import { APP_BUILD_ID } from './api';

const SEEN_KEY = 'squishy_seen_refresh_token';       // localStorage: last adopted target
const ATTEMPT_KEY = 'squishy_refresh_attempt';       // sessionStorage: target tried this session
const REFRESH_PARAM = '_squishy_refresh';

export type RefreshGate =
  | { mode: 'store-update'; title: string; message: string; updateUrl: string | null }
  | { mode: 'adopt'; target: string }
  | { mode: 'silent'; target: string }
  | { mode: 'banner'; target: string; title: string; message: string }
  | { mode: 'overlay'; target: string; title: string; message: string };

// ── safe storage (WKWebView private mode can throw) ──────────────────────────
function lsGet(k: string): string | null { try { return localStorage.getItem(k); } catch { return null; } }
function lsSet(k: string, v: string): void { try { localStorage.setItem(k, v); } catch { /* ignore */ } }
function ssGet(k: string): string | null { try { return sessionStorage.getItem(k); } catch { return null; } }
function ssSet(k: string, v: string): void { try { sessionStorage.setItem(k, v); } catch { /* ignore */ } }

/** The identity we dedupe on: a pinned web build, else the force-refresh token. */
function computeTarget(policy: AppPolicy): string | null {
  if (policy.minWebBuild && APP_BUILD_ID !== policy.minWebBuild) return `build:${policy.minWebBuild}`;
  if (policy.forceRefreshEnabled && policy.refreshToken) return `token:${policy.refreshToken}`;
  return null;
}

export function getRefreshGate(policy: AppPolicy | undefined, nativeVersion: string | null): RefreshGate | null {
  if (!policy) return null;
  const platform = Capacitor.getPlatform();

  // Native binary too old -> send to the store (blocking; unchanged behavior).
  if (platform === 'ios' && policy.minIosVersion && nativeVersion && compareVersions(nativeVersion, policy.minIosVersion) < 0) {
    return { mode: 'store-update', title: policy.title, message: policy.message, updateUrl: policy.iosUpdateUrl };
  }
  if (platform === 'android' && policy.minAndroidVersion && nativeVersion && compareVersions(nativeVersion, policy.minAndroidVersion) < 0) {
    return { mode: 'store-update', title: policy.title, message: policy.message, updateUrl: policy.androidUpdateUrl };
  }

  const target = computeTarget(policy);
  if (!target) return null;

  const seen = lsGet(SEEN_KEY);
  if (seen === null) return { mode: 'adopt', target };  // first run: adopt silently, no UI
  if (seen === target) return null;                     // already on the newest bundle

  // Existing client on an older bundle -> deliver per the admin-chosen mode.
  if (policy.refreshMode === 'banner') {
    return { mode: 'banner', target, title: policy.title, message: policy.message };
  }
  // 'force' (Instant): silent refresh, unless we already tried this session (loop guard).
  if (ssGet(ATTEMPT_KEY) === target) {
    return { mode: 'overlay', target, title: policy.title, message: policy.message };
  }
  return { mode: 'silent', target };
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((part) => Number(part) || 0);
  const pb = b.split('.').map((part) => Number(part) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

/** Record that we've silently seen this target (first-run adopt, no reload). */
export function adoptRefreshTarget(target: string): void {
  lsSet(SEEN_KEY, target);
}

/** Clear caches, mark the target adopted + attempted, and hard-reload cache-busted. */
export async function hardRefresh(target: string): Promise<void> {
  ssSet(ATTEMPT_KEY, target);   // loop guard: we've now tried this target this session
  lsSet(SEEN_KEY, target);      // adopt so we don't re-trigger after the reload
  if ('caches' in window) {
    const keys = await caches.keys().catch(() => [] as string[]);
    await Promise.all(keys.map((key) => caches.delete(key))).catch(() => {});
  }
  const url = new URL(window.location.href);
  url.searchParams.set(REFRESH_PARAM, target.replace(/[^a-zA-Z0-9]/g, ''));
  window.location.replace(url.toString());
}

// ── Interruption holds ───────────────────────────────────────────────────────
// Components register a hold around moments that must never be reloaded (a live
// study-group game, an in-flight upload). A pending silent refresh waits until
// every hold is released, then fires.
const holds = new Set<string>();
const releaseListeners = new Set<() => void>();

export function holdRefresh(tag: string): void { holds.add(tag); }
export function releaseRefresh(tag: string): void {
  holds.delete(tag);
  if (holds.size === 0) for (const cb of Array.from(releaseListeners)) cb();
}
export function isRefreshHeld(): boolean { return holds.size > 0; }
export function onRefreshReleased(cb: () => void): () => void {
  releaseListeners.add(cb);
  return () => releaseListeners.delete(cb);
}
